import { getStrings, homeShellString } from "../i18n/strings";

type Strings = ReturnType<typeof getStrings>;

// Mirrors NetRiskScanIndexClassifier.BandEdges ([40, 60, 75, 90]) on the backend — display-only, the
// frontend never computes a band itself (see utils/riskScore.ts's resolveIndexBand). Keep these two
// in sync if the backend edges ever change.
const INDEX_BANDS: Array<{ key: "high_risk" | "poor" | "fair" | "good" | "excellent"; range: string }> = [
  { key: "high_risk", range: "0-39" },
  { key: "poor", range: "40-59" },
  { key: "fair", range: "60-74" },
  { key: "good", range: "75-89" },
  { key: "excellent", range: "90-100" },
];

// "How the NetRiskScan Index works" — states the band scale plus the four evidence categories the
// Index combines, and heads off the three most common misreadings (VPN, datacenter, null-vs-false)
// in plain language. Pure/stateless: also rendered at build time into web/index.html's static
// markup (see vite.config.ts). Never recomputes or restates exact scoring weights — see spec's
// "不需要公开精确评分权重".
export function HomeIndexSection({ strings, learnMoreHref }: { strings: Strings; learnMoreHref: string }) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);

  const pillars: Array<{ title: string; text: string }> = [
    { title: t("homeIndexPillarNetworkTitle"), text: t("homeIndexPillarNetworkText") },
    { title: t("homeIndexPillarAnonymityTitle"), text: t("homeIndexPillarAnonymityText") },
    { title: t("homeIndexPillarReputationTitle"), text: t("homeIndexPillarReputationText") },
    { title: t("homeIndexPillarThreatTitle"), text: t("homeIndexPillarThreatText") },
  ];

  return (
    <section className="home-index-explainer" aria-labelledby="home-index-title">
      <h2 id="home-index-title" className="home-section-title">{t("homeIndexSectionTitle")}</h2>

      <dl className="home-index-legend">
        {INDEX_BANDS.map((band) => (
          <div className="home-index-legend__item" key={band.key}>
            <dt>{band.range}</dt>
            <dd>{strings.indexBandNames[band.key] ?? band.key}</dd>
          </div>
        ))}
      </dl>
      <p className="home-index-legend__hint">{t("homeIndexLegendHint")}</p>

      <p className="home-index-explainer__intro">{t("homeIndexSectionIntro")}</p>
      <div className="home-index-explainer__grid">
        {pillars.map((pillar) => (
          <div className="home-index-explainer__pillar" key={pillar.title}>
            <h3>{pillar.title}</h3>
            <p>{pillar.text}</p>
          </div>
        ))}
      </div>
      <p className="home-index-explainer__footer">{t("homeIndexFooter")}</p>

      <ul className="home-index-explainer__notes">
        <li>{t("homeIndexNoteVpn")}</li>
        <li>{t("homeIndexNoteDatacenter")}</li>
        <li>{t("homeIndexNoteUnknown")}</li>
      </ul>

      <a className="home-index-explainer__learn-more" href={learnMoreHref}>
        {t("homeIndexLearnMore")} <span aria-hidden="true">→</span>
      </a>
    </section>
  );
}
